// Service layer — mirrors src/fuel_tanks/fuel_tanks.ts.
//
// The paginated list reads the `data` envelope key: app/pagination.py declares
//   class Page(BasePage[T]): fields = {"items": {"alias": "data"}}
// with the comment "JSON:API 1.0 specification says that result key should be
// `data`". Reading `.items` is the recurring v6 zero-rows trap — the grid
// renders empty with no error and nothing in the console.
//
// The LIMITATIONS route is different and must not be given the same treatment:
// api.py declares `response_model = list[schemas.InspectionLimitationList]`,
// a bare array with no envelope at all.
import { AxiosResponse } from "axios";
import { CacheAxiosResponse, CacheRequestConfig } from "axios-cache-interceptor";
import queryString from "query-string";

import { axios } from "../api";
import type { AeronetV6ItemsResponse, AeronetV6Response } from "../types";

import type {
  AircraftInspectionLimitation,
  CompletionUser,
  InspectionCompletionPayload,
  InspectionCompletionResult,
  InspectionDetail,
  InspectionLimitation,
  InspectionLimitationEditPayload,
  InspectionLimitationEditResult,
  InspectionListRow,
  InspectionPayload,
  InspectionAircraftLink,
  InspectionAircraftLinkResult,
  InspectionAircraftListRow,
  InspectionAtaHistoryRow,
  InspectionBomRow,
  InspectionConfigListRow,
  InspectionLinkedTo,
  InspectionRecordTabs,
  InspectionSpecialTask,
  InspectionTaskList,
} from "./types";

export async function getInspections(
  params: any,
  axiosConfig?: CacheRequestConfig
): Promise<{ total: number; rows: InspectionListRow[] }> {
  const resp: AxiosResponse<AeronetV6Response<InspectionListRow>> =
    await axios.get("/v6/inspection", {
      params,
      paramsSerializer: (p) => queryString.stringify(p),
      ...axiosConfig,
      id: "get-inspections-tr-list",
    });
  axios.storage.remove("get-inspections-tr-list");
  return { total: resp.data.total, rows: [...resp.data.data] };
}

export function getInspection(
  id: number
): Promise<CacheAxiosResponse<InspectionDetail>> {
  return axios.get<InspectionDetail>(`/v6/inspection/${id}`, { cache: false });
}

export function createInspection(
  payload: InspectionPayload
): Promise<AxiosResponse<InspectionDetail>> {
  return axios.post<InspectionDetail>("/v6/inspection", payload);
}

export function updateInspection(
  id: number,
  payload: InspectionPayload
): Promise<AxiosResponse<InspectionDetail>> {
  return axios.put<InspectionDetail>(`/v6/inspection/${id}`, payload);
}

/** SOFT delete — crud.inspection_delete sets active=0 (the list filters active==1). */
export function deleteInspection(id: number): Promise<AxiosResponse<void>> {
  return axios.delete(`/v6/inspection/${id}`);
}

/** Bare array response — NO `.data` envelope. See the note at the top. */
export async function getInspectionLimitations(
  inspectionId: number
): Promise<InspectionLimitation[]> {
  const resp: AxiosResponse<InspectionLimitation[]> = await axios.get(
    `/v6/inspection/limitations/${inspectionId}`,
    { cache: false }
  );
  return resp.data ?? [];
}

/** SOFT delete — the route sets the limitation's active=0 and returns 204. */
export function deleteInspectionLimitation(
  limitationId: number
): Promise<AxiosResponse<void>> {
  return axios.delete(`/v6/inspection/limitations/${limitationId}`);
}

/**
 * The AIRCRAFT-scoped limitation log — every limitation recorded against one
 * aircraft, with its definition joined in.
 *
 * THIRD ENVELOPE ON THIS MODULE. Keep all three straight:
 *   GET /v6/inspection                      -> Page aliased to `data`   (getInspections)
 *   GET /v6/inspection/limitations/{id}     -> bare array, no envelope  (getInspectionLimitations)
 *   GET /v6/aircraft/inspections/{id}       -> Page served as `items`   (this)
 *
 * The third is NOT the `data` alias: `app/aircraft/api.py` returns
 * fastapi-pagination's Page directly rather than app/pagination.py's aliased
 * subclass, so the rows arrive under `items`. This is read verbatim from the
 * call the aircraft tab has always made; it is not a new contract.
 *
 * CACHE: the `id` + `axios.storage.remove` pair is carried over unchanged —
 * the tab re-reads this route immediately after every create/edit/delete, and
 * a cached hit there shows the pre-write rows.
 */
export async function getAircraftInspectionLimitations(
  aircraftId: number,
  params: { page: number; size: number },
  cacheId = "get-aircraft-inspection"
): Promise<{ rows: AircraftInspectionLimitation[]; total: number }> {
  const resp: AxiosResponse<AeronetV6ItemsResponse<AircraftInspectionLimitation>> =
    await axios.get(`/v6/aircraft/inspections/${aircraftId}`, {
      params,
      id: cacheId,
    });
  axios.storage.remove(cacheId);
  return { rows: [...resp.data.items], total: resp.data.total ?? 0 };
}

/**
 * Every limitation on the aircraft, one server page (the API caps a page at
 * 100) after another. Sortable mode needs them all: the saved order is one
 * string for the whole aircraft, so saving from one page drops every other
 * page's rows out of their sections.
 */
export async function getAllAircraftInspectionLimitations(
  aircraftId: number
): Promise<{ rows: AircraftInspectionLimitation[]; total: number }> {
  const rows: AircraftInspectionLimitation[] = [];
  for (let page = 1; ; page++) {
    const batch = await getAircraftInspectionLimitations(
      aircraftId,
      { page, size: 100 },
      `get-aircraft-inspection-all-${page}`
    );
    rows.push(...batch.rows);
    if (batch.rows.length === 0 || rows.length >= batch.total) {
      return { rows, total: rows.length };
    }
  }
}

/**
 * "Complete Inspection/s" — POST /v6/inspection/limitations/complete.
 *
 * FOURTH SHAPE ON THIS MODULE, and it is none of the other three: a BARE ARRAY
 * of per-row results, one per limitation touched. Read `.data` directly; there
 * is no `items` and no `data` envelope (api.py declares
 * `response_model=list[schemas.InspectionCompletionResult]`).
 *
 * The caller must inspect the rows rather than treating a 200 as success — see
 * `next_due_written` on `InspectionCompletionResult`.
 *
 * ROUTE ORDER: the backend declares this ABOVE `/limitations/{inspection_id}`
 * and `/{inspection_id}`, because FastAPI matches in declaration order and
 * either converter would otherwise swallow the literal "complete" and 422.
 */
export async function completeInspectionLimitations(
  payload: InspectionCompletionPayload
): Promise<InspectionCompletionResult[]> {
  const resp: AxiosResponse<InspectionCompletionResult[]> = await axios.post(
    "/v6/inspection/limitations/complete",
    payload
  );
  // The aircraft-scoped log is re-read straight after this, and a cached hit
  // there shows the PRE-completion next-due. Same trap the create/edit/delete
  // paths already pay for in `getAircraftInspectionLimitations`.
  axios.storage.remove("get-aircraft-inspection");
  return resp.data ?? [];
}

/**
 * The aircraft Inspections tab's inline edit of ONE limitation line —
 * PATCH /v6/inspection/limitations/{id} with one of last_date / next_date /
 * last_units / next_units. Last is a correction (the server recalculates Next
 * with the Complete flow's maths, no completion recorded); Next is an override.
 * Both write a limitation_history audit row. A 422 names the refusal (On
 * Condition, wrong axis) in `detail`.
 */
export async function editInspectionLimitation(
  id: number,
  payload: InspectionLimitationEditPayload
): Promise<InspectionLimitationEditResult> {
  const resp: AxiosResponse<InspectionLimitationEditResult> = await axios.patch(
    `/v6/inspection/limitations/${id}`,
    payload,
    // A failure here is an answer, not a wake: a re-sent PATCH writes a second
    // audit row into the maintenance record (the cold-start ladder, api.ts).
    { noColdStartRetry: true }
  );
  axios.storage.remove("get-aircraft-inspection");
  return resp.data;
}

/**
 * ACTIONS → Remove extension on the aircraft Inspections tab (Shelley SOP S6, 2026-10-14):
 * the ticked lines' extension, its reason and its approver back to none (legacy's remove
 * writes 0 / '' / 0). Until now the only route was the "Remove extension" tick inside
 * Complete Inspection.
 *
 * The server is Aeronet.app afa9752b (the sibling BE lane, 2026-10-14): POST
 * /aircraft/{id}/inspections/extensions/remove {log_ids} -> InspectionExtensionResult, as the
 * Add extension PUT ({updated, unchanged, lines}); behind Add extension's gate, every id this
 * aircraft's active line or nothing is written, a line with no extension left alone.
 */
export const REMOVE_INSPECTION_EXTENSIONS_PATH = (aircraftId: number) =>
  `/v6/aircraft/${aircraftId}/inspections/extensions/remove`;

export interface InspectionExtensionsRemoved {
  /** The lines whose extension was cleared. */
  updated: number[];
  /** Ticked lines that carried none. */
  unchanged: number[];
}

export async function removeInspectionExtensions(
  aircraftId: number,
  logIds: number[]
): Promise<InspectionExtensionsRemoved> {
  const resp: AxiosResponse<InspectionExtensionsRemoved> = await axios.post(
    REMOVE_INSPECTION_EXTENSIONS_PATH(aircraftId),
    { log_ids: logIds }
  );
  axios.storage.remove("get-aircraft-inspection");
  return resp.data ?? { updated: [], unchanged: [] };
}

/**
 * The "Completed By" choices for completing an inspection on an aircraft
 * (legacy aircraft_user_drop_down). A BARE ARRAY, uncached: who may sign
 * changes with group edits and the answer must be current.
 */
export async function getCompletionUsers(aircraftId: number): Promise<CompletionUser[]> {
  const resp = await axios.get<CompletionUser[]>(
    "/v6/inspection/limitations/complete/users",
    { params: { aircraft_id: aircraftId }, cache: false }
  );
  return Array.isArray(resp?.data) ? resp.data : [];
}

export async function getAircraftInspectionSortables(
  aircraftId: number
): Promise<string> {
  // No catch: "" means "no saved order", so a failed read must not look like one.
  const resp = await axios.get<{ sortstring: string }>(
    `/v6/aircraft/${aircraftId}/inspections/sortables`,
    { cache: false }
  );
  return resp.data?.sortstring || "";
}

export async function saveAircraftInspectionSortables(
  aircraftId: number,
  sortstring: string
): Promise<void> {
  await axios.put(`/v6/aircraft/${aircraftId}/inspections/sortables`, {
    sortstring,
  });
}


// ── The inspection RECORD's legacy tabs (H98) ─────────────────────────────
// app/inspections/record_tabs.py. Read-only; each returns a bare array or the
// named object (no Page envelope). `cache: false` — a record's tabs must show
// what the database holds now, not what the last visit cached.

const readTab = async <T>(path: string, inspectionId: number): Promise<T> =>
  (await axios.get<T>(`/v6/inspection/${path}/${inspectionId}`, { cache: false })).data;

export const getInspectionRecordTabs = (id: number) =>
  readTab<InspectionRecordTabs>("record-tabs", id);
export const getInspectionAircraftList = async (id: number) =>
  (await readTab<InspectionAircraftListRow[]>("aircraft-list", id)) ?? [];
/** The Aircraft List tab's (+): link airframes to this inspection (crud.link_aircraft). */
export const linkInspectionAircraft = async (id: number, body: InspectionAircraftLink) =>
  (await axios.post<InspectionAircraftLinkResult>(`/v6/inspection/aircraft-list/${id}`, body)).data;
export const getInspectionConfigList = async (id: number) =>
  (await readTab<InspectionConfigListRow[]>("config-list", id)) ?? [];
export const getInspectionAtaHistory = async (id: number) =>
  (await readTab<InspectionAtaHistoryRow[]>("ata-history", id)) ?? [];
export const getInspectionLinkedTo = (id: number) =>
  readTab<InspectionLinkedTo>("linked-to", id);
export const getInspectionBom = async (id: number) =>
  (await readTab<InspectionBomRow[]>("bom", id)) ?? [];
/** The BOM tab's writers (legacy ajax/aircraft_templates_bom.inc). A part already on the BOM is a 409. */
export const addInspectionBomPart = async (id: number, partId: number, quantity: string) =>
  (await axios.post<InspectionBomRow>(`/v6/inspection/bom/${id}`, { part_id: partId, quantity })).data;
export const setInspectionBomQuantity = async (id: number, bomId: number, quantity: string) =>
  (await axios.put<InspectionBomRow>(`/v6/inspection/bom/${id}/${bomId}`, { quantity })).data;
/** Soft: the line goes inactive, the part is untouched. */
export const removeInspectionBomLine = async (id: number, bomId: number) => {
  await axios.delete(`/v6/inspection/bom/${id}/${bomId}`);
};
export const getInspectionSpecialTasks = async (id: number) =>
  (await readTab<InspectionSpecialTask[]>("special-tasks", id)) ?? [];
export const getInspectionTaskList = (id: number) =>
  readTab<InspectionTaskList>("task-list", id);
